import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendEmailVerification, signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { Mail, RefreshCw, LogOut } from 'lucide-react';

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [resendBusy, setResendBusy] = useState(false);
  const [message, setMessage] = useState('');

  const checkVerified = useCallback(async () => {
    const u = auth.currentUser;
    if (!u) {
      navigate('/login', { replace: true });
      return;
    }
    try {
      await u.reload();
      if (u.emailVerified) {
        setStatus('verified');
        navigate('/dashboard', { replace: true });
        return;
      }
      setStatus('pending');
    } catch (e) {
      console.error(e);
      setStatus('pending');
    }
  }, [navigate]);

  useEffect(() => {
    checkVerified();
  }, [checkVerified]);

  const handleResend = async () => {
    const u = auth.currentUser;
    if (!u) return;
    setResendBusy(true);
    setMessage('');
    try {
      await sendEmailVerification(u);
      setMessage('Verification email sent. Check your inbox and spam folder.');
    } catch (e) {
      console.error(e);
      setMessage(e.message || 'Could not send email. Try again shortly.');
    } finally {
      setResendBusy(false);
    }
  };

  const handleRecheck = async () => {
    setMessage('');
    await checkVerified();
    const u = auth.currentUser;
    if (u && !u.emailVerified) {
      setMessage('Still not verified. Open the link in the email we sent, then click “I’ve verified”.');
    }
  };

  const handleSignOut = async () => {
    await signOut(auth);
    navigate('/login', { replace: true });
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <p className="text-slate-600 text-sm">Checking verification…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
        <div className="w-14 h-14 bg-[#043132]/10 text-[#043132] rounded-full flex items-center justify-center mx-auto mb-4">
          <Mail size={28} />
        </div>
        <h1 className="text-xl font-bold text-slate-800 mb-2">Verify your email</h1>
        <p className="text-sm text-slate-600 mb-6">
          We’ve sent a verification link to <strong>{auth.currentUser?.email || 'your email'}</strong>. You need to verify before
          continuing. If you use Google sign-in, your email is usually verified automatically. Click “I’ve verified” to refresh.
        </p>
        {message && <p className="text-sm text-slate-700 mb-4 bg-slate-50 rounded-lg p-3">{message}</p>}
        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={handleRecheck}
            className="w-full py-3 px-4 bg-[#043132] hover:bg-[#065354] text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            <RefreshCw size={18} /> I’ve verified, continue
          </button>
          <button
            type="button"
            onClick={handleResend}
            disabled={resendBusy}
            className="w-full py-3 px-4 border border-slate-200 text-slate-700 font-medium rounded-xl hover:bg-slate-50 disabled:opacity-50"
          >
            {resendBusy ? 'Sending…' : 'Resend verification email'}
          </button>
          <button
            type="button"
            onClick={handleSignOut}
            className="w-full py-2 text-sm text-slate-500 flex items-center justify-center gap-2 hover:text-slate-700"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
